import type { ReactNode } from 'react';
import { Button } from '@components/primitives/Button/Button';
import {
  AccessTime as WaitIcon,
  CalendarToday as AppointmentIcon,
  Close as CloseIcon,
  PersonAddAlt1 as ActiveCallIcon,
  VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import { Chip } from '@jotpsych/ui';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import type { WaitingRoomEntry } from '../types';
import { formatWaitingRoomWait } from '../utils';

import {
  buildWaitingRoomAdmitDestinations,
  type WaitingRoomAdmitActiveCallDestination,
  type WaitingRoomAdmitAppointmentDestination,
  type WaitingRoomAdmitDestination,
} from './waitingRoomAdmitDestinations';

interface SubjectLanguageCopy {
  singular: string;
  singularCap: string;
}

interface WaitingRoomAdmitDestinationModalProps {
  open: boolean;
  entry: WaitingRoomEntry;
  appointments?: WaitingRoomAdmitAppointmentDestination[];
  activeCall?: WaitingRoomAdmitActiveCallDestination | null;
  subjectLanguage: SubjectLanguageCopy;
  skipPreCallLobby: boolean;
  isLoading?: boolean;
  isCommitting?: boolean;
  errorMessage?: string | null;
  onSkipPreCallLobbyChange: (value: boolean) => void;
  onSubmit: (destination: WaitingRoomAdmitDestination) => void;
  onClose: () => void;
}

const formatDob = (dob?: string | null) => {
  if (!dob) {
    return undefined;
  }

  const date = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return dob;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getDestinationIcon = (destination: WaitingRoomAdmitDestination): ReactNode => {
  switch (destination.type) {
    case 'active_call':
      return <ActiveCallIcon fontSize="small" />;
    case 'appointment':
      return <AppointmentIcon fontSize="small" />;
    case 'walk_in':
      return <VideoCallIcon fontSize="small" />;
  }
};

const getDestinationEyebrow = (destination: WaitingRoomAdmitDestination) => {
  switch (destination.type) {
    case 'active_call':
      return 'Active call';
    case 'appointment':
      return 'Scheduled visit';
    case 'walk_in':
      return 'New encounter';
  }
};

export function WaitingRoomAdmitDestinationModal({
  open,
  entry,
  appointments = [],
  activeCall,
  subjectLanguage,
  skipPreCallLobby,
  isLoading = false,
  isCommitting = false,
  errorMessage,
  onSkipPreCallLobbyChange,
  onSubmit,
  onClose,
}: WaitingRoomAdmitDestinationModalProps) {
  const destinations = buildWaitingRoomAdmitDestinations({
    entry,
    appointments,
    activeCall,
    subjectLanguage,
  });
  const dobLabel = formatDob(entry.patient_dob);

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={isCommitting ? undefined : onClose}>
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              Admit from waiting room
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Choose where {entry.patient_name} should join
            </Typography>
          </Box>
          <IconButton
            aria-label="Close admit destination picker"
            disabled={isCommitting}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && <LinearProgress />}

        <Box sx={{ px: 3, py: 2.5, bgcolor: 'grey.50' }}>
          <Stack spacing={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {entry.patient_name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 0.75 }}
                >
                  {dobLabel && <Chip size="small" label={`DOB ${dobLabel}`} />}
                  <Chip
                    size="small"
                    icon={<WaitIcon fontSize="small" />}
                    label={formatWaitingRoomWait(entry.wait_seconds)}
                    color="success"
                    variant="soft"
                  />
                </Stack>
              </Box>
            </Stack>

            {entry.reason_for_visit && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {entry.reason_for_visit}
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack spacing={1.25} sx={{ p: 3 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {destinations.map(destination => (
            <Button
              key={
                destination.type === 'appointment'
                  ? destination.appointment.appointmentId
                  : destination.type
              }
              variant={
                destination.type === 'active_call' || destination.type === 'walk_in'
                  ? 'contained'
                  : 'outlined'
              }
              color={destination.type === 'active_call' ? 'secondary' : 'primary'}
              disabled={isLoading || isCommitting}
              onClick={() => onSubmit(destination)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                '& .MuiButton-startIcon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                },
              }}
              startIcon={getDestinationIcon(destination)}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: destination.type === 'appointment' ? 'text.secondary' : 'inherit',
                    fontWeight: 800,
                    lineHeight: 1.2,
                    opacity: destination.type === 'appointment' ? 1 : 0.82,
                  }}
                >
                  {getDestinationEyebrow(destination)}
                </Typography>
                <Typography component="span" sx={{ display: 'block', fontWeight: 800 }}>
                  {destination.label}
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    display: 'block',
                    color: destination.type === 'appointment' ? 'text.secondary' : 'inherit',
                    opacity: destination.type === 'appointment' ? 1 : 0.82,
                  }}
                >
                  {destination.description}
                </Typography>
              </Box>
            </Button>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={skipPreCallLobby}
              disabled={isCommitting}
              onChange={event => onSkipPreCallLobbyChange(event.target.checked)}
            />
          }
          label="Skip device check next time"
        />
        <Button variant="text" disabled={isCommitting} onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
